import React from 'react';
import { useAtomValue } from 'jotai';
import { getContext } from '@fiverr-private/fiverr_context';
import { logger } from '@fiverr-private/obs';
import { useSellerData, type SellerPageFederatedSeller } from '@fiverr-private/seller_pages_toolbox';
import { getCanonicalUrl } from '../../../server/utils/getCanonicalUrl';
import { gigsAtom, reviewsDataAtom } from '../../atoms';
import { buildOrganizationJsonLd, buildProfilePageJsonLd } from './buildStructuredData';

// `<` is escaped so embedded values (e.g. `</script>` in a seller bio) can't break out of the tag.
const toJsonLd = (data: object): string => JSON.stringify(data).replace(/</g, '\\u003c');

const selectSeller = (sellerData: SellerPageFederatedSeller) => sellerData;

/**
 * Renders schema.org JSON-LD for the seller profile. Runs at render time (not in a middleware) so
 * i18n-dependent fields (e.g. category names) resolve within the request's locale context.
 */
export const StructuredData = () => {
    const seller = useSellerData(selectSeller) as SellerPageFederatedSeller | undefined;
    const reviewsData = useAtomValue(reviewsDataAtom);
    const gigs = useAtomValue(gigsAtom);
    const { url } = getContext();

    const markup = React.useMemo(() => {
        if (!seller || !url) {
            return null;
        }
        try {
            const canonicalUrl = getCanonicalUrl(url);
            return {
                profilePage: toJsonLd(buildProfilePageJsonLd({ seller, reviewsData, gigs, canonicalUrl })),
                organization: toJsonLd(buildOrganizationJsonLd()),
            };
        } catch (originalError) {
            logger.error(new Error('Failed to build seller profile structured data'), { cause: originalError });
            return null;
        }
    }, [seller, reviewsData, gigs, url]);

    if (!markup) {
        return null;
    }

    return (
        <>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: markup.profilePage }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: markup.organization }} />
        </>
    );
};
